'use client';

import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { FieldPath, FieldValues } from 'react-hook-form';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectSeparator,
  SelectTrigger
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { BaseFormFieldProps, FormOption } from '@/types/base-form';

// Sentinel value for the inline "Clear" item. Empty string is reserved by
// Radix Select for "no value", so it can't be used as a SelectItem value.
const CLEAR_SENTINEL = '__form_select_clear__';

interface FormSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends BaseFormFieldProps<TFieldValues, TName> {
  options: FormOption[];
  placeholder?: string;
  searchable?: boolean;
  /**
   * When true and a value is set, the dropdown shows a "Clear" item at the
   * top (with a separator) so the user can reset the field to empty
   * without any extra UI on the trigger. Required selects look identical
   * to today, so this is safe to opt every consumer into.
   *
   * Pass `clearLabel` to override the default ('common.clear').
   */
  clearable?: boolean;
  clearLabel?: string;
}

function FormSelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>({
  control,
  name,
  label,
  description,
  required,
  options,
  placeholder = 'Select an option',
  disabled,
  className,
  clearable = false,
  clearLabel
}: FormSelectProps<TFieldValues, TName>) {
  const t = useTranslations('common');
  const resolvedClearLabel = clearLabel ?? t('clear');

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        const selectedLabel = field.value
          ? (options.find((o) => o.value === field.value)?.label ?? field.value)
          : null;

        const showClear = clearable && !!field.value && !disabled;

        const handleValueChange = (next: string) => {
          if (next === CLEAR_SENTINEL) {
            field.onChange('');
            return;
          }
          field.onChange(next);
        };

        return (
          <FormItem className={className}>
            {label && (
              <FormLabel>
                {label}
                {required && <span className='ms-1 text-red-500'>*</span>}
              </FormLabel>
            )}
            <Select
              onValueChange={handleValueChange}
              value={field.value ?? ''}
              disabled={disabled}
            >
              <FormControl>
                <SelectTrigger>
                  <span
                    className={cn(
                      'flex-1 truncate text-start',
                      !selectedLabel && 'text-muted-foreground'
                    )}
                  >
                    {selectedLabel ?? placeholder}
                  </span>
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {showClear ? (
                  <SelectGroup>
                    <SelectItem
                      value={CLEAR_SENTINEL}
                      className='text-muted-foreground focus:text-foreground'
                    >
                      <span className='inline-flex items-center gap-2'>
                        <X className='size-3.5' />
                        {resolvedClearLabel}
                      </span>
                    </SelectItem>
                    <SelectSeparator />
                  </SelectGroup>
                ) : null}
                {options.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}

export { FormSelect, type FormOption };
